import c from 'classnames';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { urls } from '@repo/app-constants';
import { kitResultsViewable } from '@repo/utils';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { useSelectedProfilePortalConfig } from 'hooks/useSelectedProfilePortalConfig';
import { useShouldShowConsults } from 'hooks/useShouldShowConsultLink';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import useFeature from 'services/features/useFeature';
import { getShouldHideConsults } from 'store/kits';
import { getImpersonateId } from 'store/user';

import { Typography } from '@repo/ui';

import { LINKS } from 'assets';
import styles from './RecommendedForYou.module.css';
import { GuidesForYou } from './components/GuidesForYou';
import { RecommendedCard } from './components/RecommendedCard';
import { useRecommendedCards } from './hooks/useRecommendedCards';
import {
  getRecommendedForYouCards,
  UpsellCardKind,
} from './utils/getRecommendedForYouCards';

export function RecommendedForYou({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) {
  const { isFeatureEnabled: isPurchaseKitUpsellEnabled } = useFeature(
    'purchase_kit_upsell',
  );
  const { latestKit, hasMembership, getActiveStep } =
    useTinyAccountForHome(tinyAccountId);
  const { checkoutUrl } = useCheckoutKit({
    kitType: latestKit?.sampling_type,
    nonMemberUrl: isPurchaseKitUpsellEnabled ? LINKS.purchaseKit : urls.STORE,
  });
  const impersonateId = useSelector(getImpersonateId);
  const { config: portalConfig, isLoading: isPortalConfigLoading } =
    useSelectedProfilePortalConfig();
  const shouldShowConsults = useShouldShowConsults(latestKit);
  const shouldHideConsults = useSelector(getShouldHideConsults(latestKit?.id));

  const hideUpsells = !!portalConfig.hide_upsells || isPortalConfigLoading;
  const { trackEventOnce } = useAnalytics();
  const { isFeatureEnabled: isTinyPlusUpsellEnabled } = useFeature(
    'tiny_plus_upsell_moments',
  );

  const resolvedUpsellCard: UpsellCardKind | null = isPortalConfigLoading
    ? null
    : !isTinyPlusUpsellEnabled || !!hasMembership || !!portalConfig.hide_upsells
      ? shouldHideConsults
        ? 'facebook'
        : shouldShowConsults
          ? 'consult'
          : 'consultAlt'
      : 'upsellTinyPlus';

  const RECOMMENDED_FOR_YOU_CARDS = useRecommendedCards({
    latestKitId: latestKit?.id,
    checkoutUrl,
    isCheckoutExternal: !isPurchaseKitUpsellEnabled || !!hasMembership,
  });

  const cards = getRecommendedForYouCards(
    RECOMMENDED_FOR_YOU_CARDS,
    getActiveStep,
    resolvedUpsellCard,
    hideUpsells,
  );

  useEffect(() => {
    if (resolvedUpsellCard === 'upsellTinyPlus') {
      trackEventOnce('Tiny Plus Contextual Card Viewed', 'homepage', {
        placement: 'homepage',
      });
    }
  }, [resolvedUpsellCard, trackEventOnce]);

  //If the surveys are completed and results are not ready we show guides instead of the other cards
  if (
    latestKit?.date_survey_completed &&
    !kitResultsViewable(latestKit, !!impersonateId)
  )
    return <GuidesForYou />;

  return (
    <div className={c(styles.container)}>
      <Typography variant='heading-l'>Recommended for you</Typography>
      <div className={styles.cardsContainer}>
        {cards.map((card, index) => (
          <RecommendedCard
            key={index}
            cardsLength={cards.length}
            {...card}
            index={index}
          />
        ))}
      </div>
    </div>
  );
}
